import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  CreateFundingBatchRequest,
  UpstreamAccountRecord,
  UpstreamFundingBatchRecord,
} from '@nadoutong/contracts';
import { OpsApiError } from '../../api/client.ts';
import {
  useCommitFundingBatch,
  useCreateFundingBatch,
  useFundingBatches,
  useUpdateFundingBatch,
} from '../../api/funding.ts';
import { usePricing } from '../../api/pricing.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Checkbox, Input, Select } from '../../ui/index.ts';
import type { SelectOption } from '../../ui/index.ts';
import { formatCnyCents } from '../formatters.ts';
import { Modal } from '../Modal.tsx';
import { StatusChip } from '../StatusChip.tsx';
import { coerceErrorMessage, useToast } from '../Toast.tsx';

/**
 * Funding batches — active plus recent depleted batches, each with a
 * consumption bar, grouped by the account that paid.
 *
 * Lives in the funding drawer off the supply-access section (epic #1327
 * PR-6). Amounts render in CNY throughout: batches are bought in CNY at a
 * recorded deposit rate, so showing the USD capacity would be a converted
 * figure sitting next to the real one an operator paid.
 */

interface FundingCardProps {
  readonly upstreamId: string;
  readonly accounts: readonly UpstreamAccountRecord[];
}

function formatRate(rate: number): string {
  return rate.toFixed(4);
}

export function FundingCard({ upstreamId, accounts }: FundingCardProps): JSX.Element {
  const t = useT();
  const batches = useFundingBatches(upstreamId);
  const commitMut = useCommitFundingBatch(upstreamId);
  const updateMut = useUpdateFundingBatch(upstreamId);
  const toast = useToast();
  const [newBatchOpen, setNewBatchOpen] = useState(false);

  const accountLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts) {
      m.set(a.id, a.label);
    }
    return m;
  }, [accounts]);

  const groupedBatches = useMemo(() => {
    const items = batches.data ?? [];
    const groups = new Map<string, UpstreamFundingBatchRecord[]>();
    for (const b of items) {
      const key = b.account_id ?? '__none__';
      const list = groups.get(key);
      if (list !== undefined) {
        list.push(b);
      } else {
        groups.set(key, [b]);
      }
    }
    return groups;
  }, [batches.data]);

  return (
    <article className="flex flex-col gap-6" data-testid="upstream-funding-card">
      <header className="flex items-center justify-between">
        <p className="m-0 text-dense text-paper-low">
          {t('upstream_supply_access.drawer.funding.description')}
        </p>
        <Button
          variant="primary"
          onClick={() => setNewBatchOpen(true)}
          disabled={accounts.length === 0}
          data-testid="upstream-funding-new"
        >
          {t('upstream_detail.funding.new')}
        </Button>
      </header>

      {batches.isPending ? (
        <p className="m-0 text-body text-paper-low" data-testid="upstream-funding-loading">
          {t('upstream_detail.funding.loading_aria')}
        </p>
      ) : null}
      {batches.isError ? (
        <p
          className="m-0 text-body text-bad"
          role="alert"
          data-testid="upstream-funding-error"
        >
          {batches.error instanceof OpsApiError
            ? `${batches.error.code}: ${batches.error.message}`
            : t('upstream_detail.funding.error.load')}
        </p>
      ) : null}
      {batches.data !== undefined && batches.data.length === 0 ? (
        <p className="m-0 text-body text-paper-low" data-testid="upstream-funding-empty">
          {t('upstream_detail.funding.empty')}
        </p>
      ) : null}
      {batches.data !== undefined && batches.data.length > 0 ? (
        <div className="flex flex-col gap-6" data-testid="upstream-funding-list">
          {Array.from(groupedBatches.entries()).map(([accountKey, accountBatches]) => (
            <div key={accountKey} className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="text-label font-medium text-paper-low">
                  {accountKey === '__none__'
                    ? t('upstream_detail.funding.account.unassigned')
                    : accountLabelMap.get(accountKey) ?? accountKey}
                </span>
              </div>
              <ul className="flex flex-col gap-3">
                {accountBatches.map((batch) => (
                  <FundingBatchRow
                    key={batch.id}
                    batch={batch}
                    onCommit={() => {
                      commitMut.mutate(batch.id, {
                        onSuccess: () => toast.success(t('upstream_detail.funding.toast.committed')),
                        onError: (err) => toast.error(coerceErrorMessage(err)),
                      });
                    }}
                    onUpdate={(body) => {
                      updateMut.mutate({ batchId: batch.id, body }, {
                        onSuccess: () => toast.success(t('upstream_detail.funding.toast.updated')),
                        onError: (err) => toast.error(coerceErrorMessage(err)),
                      });
                    }}
                    busy={commitMut.isPending || updateMut.isPending}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}

      {newBatchOpen ? (
        <NewFundingBatchModal
          upstreamId={upstreamId}
          accounts={accounts}
          onClose={() => setNewBatchOpen(false)}
        />
      ) : null}
    </article>
  );
}

interface FundingBatchRowProps {
  readonly batch: UpstreamFundingBatchRecord;
  readonly onCommit: () => void;
  readonly onUpdate: (body: Record<string, unknown>) => void;
  readonly busy: boolean;
}

function FundingBatchRow({ batch, onCommit, onUpdate, busy }: FundingBatchRowProps): JSX.Element {
  const t = useT();
  const isCorrection = batch.usd_capacity_micros < 0;
  const pct = Math.max(0, Math.min(100, batch.pct_consumed));
  const barClass =
    pct >= 95
      ? 'bg-bad'
      : pct >= 80
        ? 'bg-warn'
        : 'bg-ok';
  const [editing, setEditing] = useState(false);
  const signedCny = (cents: number): string => formatCnyCents(cents, cents < 0);

  return (
    <li
      className="flex flex-col gap-3 rounded-lg bg-well p-4"
      data-testid={`upstream-funding-batch-${batch.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-label text-paper-low" data-testid={`upstream-funding-batch-id-${batch.id}`}>
            {batch.id}
          </span>
          <span className="font-mono text-micro text-paper-mid">
            {t('upstream_detail.funding.batch.mode_rate', {
              mode: batch.mode,
              rate: formatRate(batch.effective_rate_cny_per_usd),
            })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {batch.committed ? (
            <StatusChip variant="ok" label={t('upstream_detail.funding.batch.chip.committed')} />
          ) : (
            <StatusChip variant="warning" label={t('upstream_detail.funding.batch.chip.draft')} />
          )}
          {isCorrection ? (
            <StatusChip
              variant="warning"
              label={t('upstream_detail.funding.batch.chip.correction')}
              testId={`upstream-funding-batch-correction-${batch.id}`}
            />
          ) : batch.is_depleted ? (
            <StatusChip variant="error" label={t('upstream_detail.funding.batch.chip.depleted')} />
          ) : (
            <StatusChip variant="ok" label={t('upstream_detail.funding.batch.chip.active')} />
          )}
          {batch.alerted_95_at !== null ? (
            <StatusChip variant="error" label={t('upstream_detail.funding.batch.chip.alert_95')} />
          ) : batch.alerted_80_at !== null ? (
            <StatusChip variant="warning" label={t('upstream_detail.funding.batch.chip.alert_80')} />
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <FundingMeta label={t('upstream_detail.funding.batch.meta.paid')} value={signedCny(batch.cny_paid_cents)} />
        <FundingMeta
          label={t('upstream_detail.funding.batch.meta.capacity')}
          value={signedCny(batch.cny_paid_cents)}
        />
        <FundingMeta
          label={t('upstream_detail.funding.batch.meta.consumed')}
          value={signedCny(batch.cny_consumed_cents)}
        />
        <FundingMeta label={t('upstream_detail.funding.batch.meta.funded_at')} value={batch.funded_at} mono={false} />
      </div>

      {isCorrection ? null : (
      <div className="flex flex-col gap-1">
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-raised"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          data-testid={`upstream-funding-progress-${batch.id}`}
        >
          <div
            className={`h-full ${barClass}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-right font-mono text-micro text-paper-low">
          {t('upstream_detail.funding.batch.pct_consumed', { pct: String(pct) })}
        </span>
      </div>
      )}

      {batch.upstream_invoice_ref !== null || batch.note !== null ? (
        <div className="flex flex-col gap-1 border-t border-line-soft pt-2 text-label text-paper-mid">
          {batch.upstream_invoice_ref !== null ? (
            <span>
              <span className="font-medium text-paper-low">
                {t('upstream_detail.funding.batch.invoice')}
              </span>{' '}
              · {batch.upstream_invoice_ref}
            </span>
          ) : null}
          {batch.note !== null ? (
            <span>
              <span className="font-medium text-paper-low">
                {t('upstream_detail.funding.batch.note')}
              </span>{' '}
              · {batch.note}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Actions for uncommitted batches */}
      {!batch.committed ? (
        <div className="flex items-center justify-end gap-2 border-t border-line-soft pt-3">
          {editing ? (
            <BatchEditForm
              batch={batch}
              onSave={(body) => { onUpdate(body); setEditing(false); }}
              onCancel={() => setEditing(false)}
              busy={busy}
            />
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)} disabled={busy}>
                {t('upstream_detail.funding.batch.action.edit')}
              </Button>
              <Button variant="primary" size="sm" onClick={onCommit} disabled={busy}>
                {t('upstream_detail.funding.batch.action.commit')}
              </Button>
            </>
          )}
        </div>
      ) : null}
    </li>
  );
}

function BatchEditForm({
  batch,
  onSave,
  onCancel,
  busy,
}: {
  readonly batch: UpstreamFundingBatchRecord;
  readonly onSave: (body: Record<string, unknown>) => void;
  readonly onCancel: () => void;
  readonly busy: boolean;
}): JSX.Element {
  const t = useT();
  const isCorrection = batch.usd_capacity_micros < 0;
  const [cnyYuan, setCnyYuan] = useState(String(batch.cny_paid_cents / 100));
  const [invoiceRef, setInvoiceRef] = useState(batch.upstream_invoice_ref ?? '');
  const [note, setNote] = useState(batch.note ?? '');
  const [formError, setFormError] = useState<string | null>(null);

  function handleSave(): void {
    if (isCorrection && note.trim().length === 0) {
      setFormError(t('upstream_detail.funding.error.correction_note_required'));
      return;
    }
    setFormError(null);
    const body: Record<string, unknown> = {};
    if (!isCorrection) {
      const yuan = parseFloat(cnyYuan);
      if (Number.isFinite(yuan) && yuan > 0) {
        const newCnyCents = Math.round(yuan * 100);
        if (newCnyCents !== batch.cny_paid_cents) {
          body.cny_paid_cents = newCnyCents;
          if (batch.upstream_deposit_rate !== null && batch.upstream_deposit_rate > 0) {
            body.usd_capacity_cents = Math.round(newCnyCents / batch.upstream_deposit_rate);
          }
        }
      }
    }
    const trimRef = invoiceRef.trim();
    if (trimRef !== (batch.upstream_invoice_ref ?? '')) {
      body.upstream_invoice_ref = trimRef.length > 0 ? trimRef : null;
    }
    const trimNote = note.trim();
    if (trimNote !== (batch.note ?? '')) {
      // A correction's note is required server-side. Sending null on
      // clear would 400; skip the field so an empty box is a no-op.
      if (!isCorrection || trimNote.length > 0) {
        body.note = trimNote.length > 0 ? trimNote : null;
      }
    }
    if (Object.keys(body).length > 0) {
      onSave(body);
    } else {
      onCancel();
    }
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <div className={`grid grid-cols-1 gap-3 ${isCorrection ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
        {isCorrection ? null : (
        <label className="flex flex-col gap-1">
          <span className="text-label text-paper-mid">
            {t('upstream_detail.funding.form.cny_paid_yuan.label')}
          </span>
          <Input
            type="number"
            step="0.01"
            value={cnyYuan}
            onChange={(e) => setCnyYuan(e.currentTarget.value)}
            disabled={busy}
            mono
          />
        </label>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-label text-paper-mid">
            {t('upstream_detail.funding.form.invoice_ref.label')}
          </span>
          <Input
            type="text"
            value={invoiceRef}
            onChange={(e) => setInvoiceRef(e.currentTarget.value)}
            disabled={busy}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-label text-paper-mid">
            {isCorrection
              ? t('upstream_detail.funding.form.note.required_label')
              : t('upstream_detail.funding.form.note.label')}
          </span>
          <Input
            type="text"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            disabled={busy}
          />
        </label>
      </div>
      {formError !== null ? (
        <p className="m-0 text-label text-bad" role="alert" data-testid="upstream-funding-edit-error">
          {formError}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <Button size="sm" onClick={handleSave} disabled={busy}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

interface FundingMetaProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

function FundingMeta({ label, value, mono = true }: FundingMetaProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-label text-paper-low">
        {label}
      </span>
      <span className={`text-body ${mono ? 'font-mono' : ''} text-paper`}>{value}</span>
    </div>
  );
}

interface NewFundingBatchModalProps {
  readonly upstreamId: string;
  readonly accounts: readonly UpstreamAccountRecord[];
  readonly onClose: () => void;
}

function NewFundingBatchModal({
  upstreamId,
  accounts,
  onClose,
}: NewFundingBatchModalProps): JSX.Element {
  const t = useT();
  const create = useCreateFundingBatch(upstreamId);
  const pricing = usePricing();
  const toast = useToast();
  const [accountId, setAccountId] = useState(accounts.length > 0 ? accounts[0]!.id : '');
  const [cnyPaidYuan, setCnyPaidYuan] = useState('');
  const [usdDelta, setUsdDelta] = useState('');
  const [correction, setCorrection] = useState(false);
  const [invoiceRef, setInvoiceRef] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const rate = pricing.data?.policy?.cny_per_usd ?? null;

  const accountOptions = useMemo<readonly SelectOption[]>(() => {
    if (accounts.length === 0) {
      return [{ value: '', label: t('upstream_detail.funding.form.account.none') }];
    }
    return accounts.map((a) => ({ value: a.id, label: a.label }));
  }, [accounts, t]);

  const computedUsd = useMemo(() => {
    if (rate === null) return null;
    const yuan = parseFloat(cnyPaidYuan);
    if (!Number.isFinite(yuan) || yuan <= 0) return null;
    const cnyPaidCents = Math.round(yuan * 100);
    const usdCapacityCents = Math.round(cnyPaidCents / rate);
    return usdCapacityCents / 100;
  }, [rate, cnyPaidYuan]);

  const computedCorrectionCnyCents = useMemo(() => {
    if (rate === null) return null;
    const dollars = parseFloat(usdDelta);
    if (!Number.isFinite(dollars) || dollars >= 0) return null;
    const usdCapacityCents = Math.round(dollars * 100);
    if (usdCapacityCents >= 0) return null;
    return Math.round(usdCapacityCents * rate);
  }, [rate, usdDelta]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);

    if (rate === null) {
      setFormError(t('upstream_detail.funding.error.rate_unavailable'));
      return;
    }

    if (accountId.length === 0) {
      setFormError(t('upstream_detail.funding.error.account_required'));
      return;
    }

    if (correction) {
      const dollars = parseFloat(usdDelta);
      if (!Number.isFinite(dollars) || dollars >= 0) {
        setFormError(t('upstream_detail.funding.error.usd_delta_required'));
        return;
      }
      const usdCapacityCents = Math.round(dollars * 100);
      if (usdCapacityCents >= 0) {
        setFormError(t('upstream_detail.funding.error.usd_delta_required'));
        return;
      }
      if (note.trim().length === 0) {
        setFormError(t('upstream_detail.funding.error.correction_note_required'));
        return;
      }
      const cnyPaidCents = Math.round(usdCapacityCents * rate);
      const body: CreateFundingBatchRequest = {
        mode: 'cny_to_usd',
        account_id: accountId,
        cny_paid_cents: cnyPaidCents,
        usd_capacity_cents: usdCapacityCents,
        upstream_deposit_rate: rate,
        note: note.trim(),
        ...(invoiceRef.trim().length > 0 ? { upstream_invoice_ref: invoiceRef.trim() } : {}),
      };
      try {
        const created = await create.mutateAsync(body);
        toast.success(t('upstream_detail.funding.toast.registered', { id: created.id }));
        onClose();
      } catch (err) {
        const message =
          err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
        setFormError(message);
        toast.error(message);
      }
      return;
    }

    const yuan = parseFloat(cnyPaidYuan);
    if (!Number.isFinite(yuan) || yuan <= 0) {
      setFormError(t('upstream_detail.funding.error.cny_paid_required'));
      return;
    }

    const cnyPaidCents = Math.round(yuan * 100);
    const usdCapacityCents = Math.round(cnyPaidCents / rate);

    const body: CreateFundingBatchRequest = {
      mode: 'cny_to_usd',
      account_id: accountId,
      cny_paid_cents: cnyPaidCents,
      usd_capacity_cents: usdCapacityCents,
      upstream_deposit_rate: rate,
      ...(invoiceRef.trim().length > 0 ? { upstream_invoice_ref: invoiceRef.trim() } : {}),
      ...(note.trim().length > 0 ? { note: note.trim() } : {}),
    };

    try {
      const created = await create.mutateAsync(body);
      toast.success(t('upstream_detail.funding.toast.registered', { id: created.id }));
      onClose();
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setFormError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!create.isPending) onClose();
      }}
      titleId="funding-new-title"
      testId="upstream-funding-modal"
    >
      <form className="flex flex-col gap-6 p-6" onSubmit={handleSubmit} noValidate>
        <h2 id="funding-new-title" className="m-0 text-title text-paper">
          {t('upstream_detail.funding.register.title')}
        </h2>

        <div className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_detail.funding.form.account.label')}
          </span>
          <Select
            value={accountId.length > 0 ? accountId : undefined}
            onValueChange={setAccountId}
            options={accountOptions}
            disabled={create.isPending}
            ariaLabel={t('upstream_detail.funding.form.account.label')}
            testId="upstream-funding-account"
          />
        </div>

        <Checkbox
          checked={correction}
          onCheckedChange={setCorrection}
          disabled={create.isPending}
          label={t('upstream_detail.funding.form.correction.label')}
          testId="upstream-funding-correction"
        />

        {correction ? (
          <label className="flex flex-col gap-2">
            <span className="text-label text-paper-mid">
              {t('upstream_detail.funding.form.usd_delta.label')}
            </span>
            <Input
              type="number"
              step="0.01"
              value={usdDelta}
              onChange={(e) => setUsdDelta(e.currentTarget.value)}
              data-testid="upstream-funding-usd-delta"
              disabled={create.isPending}
              autoComplete="off"
              mono
            />
          </label>
        ) : (
        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_detail.funding.form.cny_paid_yuan.label')}
          </span>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={cnyPaidYuan}
            onChange={(e) => setCnyPaidYuan(e.currentTarget.value)}
            data-testid="upstream-funding-cny-paid"
            disabled={create.isPending}
            autoComplete="off"
            mono
          />
        </label>
        )}

        <div className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_detail.funding.form.exchange_rate.label')}
          </span>
          {pricing.isLoading ? (
            <span className="text-body text-paper-low">{t('upstream_detail.funding.form.exchange_rate.loading')}</span>
          ) : rate !== null ? (
            <span className="font-mono text-body text-paper" data-testid="upstream-funding-rate">{rate.toFixed(4)}</span>
          ) : (
            <span className="text-body text-bad">{t('upstream_detail.funding.error.rate_unavailable')}</span>
          )}
        </div>

        {correction ? (
          <div className="flex flex-col gap-2">
            <span className="text-label text-paper-mid">
              {t('upstream_detail.funding.form.computed_cny.label')}
            </span>
            <span className="font-mono text-body text-paper" data-testid="upstream-funding-cny-computed">
              {computedCorrectionCnyCents !== null
                ? formatCnyCents(computedCorrectionCnyCents, computedCorrectionCnyCents < 0)
                : '—'}
            </span>
          </div>
        ) : (
        <div className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_detail.funding.form.computed_usd.label')}
          </span>
          <span className="font-mono text-body text-paper" data-testid="upstream-funding-usd-computed">
            {computedUsd !== null ? `$${computedUsd.toFixed(2)}` : '—'}
          </span>
        </div>
        )}

        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_detail.funding.form.invoice_ref.label')}
          </span>
          <Input
            type="text"
            value={invoiceRef}
            onChange={(e) => setInvoiceRef(e.currentTarget.value)}
            data-testid="upstream-funding-invoice-ref"
            disabled={create.isPending}
            autoComplete="off"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {correction
              ? t('upstream_detail.funding.form.note.required_label')
              : t('upstream_detail.funding.form.note.label')}
          </span>
          <Input
            type="text"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            data-testid="upstream-funding-note"
            disabled={create.isPending}
            autoComplete="off"
          />
        </label>

        {formError !== null ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="upstream-funding-error-form"
          >
            {formError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => {
              if (!create.isPending) onClose();
            }}
            disabled={create.isPending}
            data-testid="upstream-funding-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={create.isPending || rate === null}
            loading={create.isPending}
            data-testid="upstream-funding-submit"
          >
            {create.isPending
              ? t('upstream_detail.funding.register.submitting')
              : t('upstream_detail.funding.register.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
